import { useCallback, useEffect, useRef, useState } from "react";
import { Building2, CalendarPlus, ClipboardList, Copy, Loader2, Save, Trash2, Wrench } from "lucide-react";
import { CalidadControlDocumentalPanel } from "../../../components/control-modificaciones/CalidadControlDocumentalPanel";
import { Iso900114001Checkbox, Iso900114001Input, Iso900114001Select, Iso900114001Textarea } from "../../../components/shared/Iso900114001Fields";
import { Iso900114001Filters } from "../../../components/shared/Iso900114001Filters";
import { Iso900114001Modal } from "../../../components/shared/Iso900114001Modal";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import { Iso900114001CrudTable, type Iso900114001TableColumn } from "../../../components/shared/Iso900114001CrudTable";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001ScrollableCell } from "../../../components/shared/Iso900114001ScrollableCell";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import { getCertificationRequestError } from "../../../components/shared/request";
import { isRequestCancellation } from "@shared/api/client";
import { createInfraYear, deleteInfra, deleteInfraYear, getInfra, infraControlEndpoint, saveInfra } from "../api/mantenimiento-infraestructuras";
import { applicability, emptyInfra, months, type CopyField, type InfraResponse, type MantenimientoInfraestructura, type MantenimientoInfraestructuraPayload } from "../types/mantenimiento-infraestructuras";

type Props = { onBack: () => void };
type Tab = "mantenimiento" | "control";
const copyOptions: { id: CopyField; label: string }[] = [
  { id: "instruccion", label: "Instrucción" }, { id: "elemento", label: "Elemento" }, { id: "realizado_por", label: "Realizado por" }, { id: "periodicidad", label: "Periodicidad" }, { id: "observaciones", label: "Observaciones" }, { id: "aplicabilidad", label: "Áreas de aplicación" }, { id: "meses", label: "Valores mensuales, incluidos los X" },
];
const areaLabels: Record<string, string> = { aplica_gerencia: "Gerencia", aplica_comercial: "Comercial", aplica_ofertas: "Ofertas", aplica_otr: "OTR", aplica_facturacion: "Facturación", aplica_calidad_medioambiente: "Calidad y medioambiente" };

export function CalidadMantenimientoInfraestructurasSection({ onBack }: Props) {
  const [tab, setTab] = useState<Tab>("mantenimiento");
  const [data, setData] = useState<InfraResponse | null>(null);
  const [filters, setFilters] = useState({ anio: "", responsable: "", periodicidad: "" });
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ open: boolean; id: number | null; value: MantenimientoInfraestructuraPayload }>({ open: false, id: null, value: emptyInfra(2026) });
  const [clone, setClone] = useState<{ open: boolean; source: number; target: number; fields: CopyField[] }>({ open: false, source: 2025, target: 2026, fields: copyOptions.map((item) => item.id) });
  const [deleteRow, setDeleteRow] = useState<MantenimientoInfraestructura | null>(null);
  const [deleteYear, setDeleteYear] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestSequenceRef = useRef(0);
  const hasDataRef = useRef(false);
  const lastLoadedKeyRef = useRef("");

  useEffect(() => {
    if (searchInput === "") {
      setDebouncedSearch("");
      return;
    }

    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async (force = false) => {
    const params = {
      anio: filters.anio || undefined,
      search: debouncedSearch || undefined,
      responsable: filters.responsable || undefined,
      periodicidad: filters.periodicidad || undefined,
    };
    const requestKey = JSON.stringify({
      anio: params.anio ?? "",
      search: params.search ?? "",
      responsable: params.responsable ?? "",
      periodicidad: params.periodicidad ?? "",
    });

    if (!force && requestKey === lastLoadedKeyRef.current) {
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestSequence = ++requestSequenceRef.current;
    const isInitialLoad = !hasDataRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      const response = await getInfra(params, controller.signal);
      if (requestSequence !== requestSequenceRef.current) {
        return;
      }

      setData(response);
      hasDataRef.current = true;

      const effectiveYear = params.anio ?? response.meta.selected_year ?? "";
      lastLoadedKeyRef.current = JSON.stringify({
        anio: String(effectiveYear),
        search: params.search ?? "",
        responsable: params.responsable ?? "",
        periodicidad: params.periodicidad ?? "",
      });

      if (filters.anio === "" && response.meta.selected_year !== null) {
        setFilters((current) => current.anio === ""
          ? { ...current, anio: String(response.meta.selected_year) }
          : current);
      }
    } catch (requestError) {
      if (isRequestCancellation(requestError) || requestSequence !== requestSequenceRef.current) {
        return;
      }
      setError(getCertificationRequestError(requestError, "No se pudo cargar el mantenimiento de infraestructuras."));
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [debouncedSearch, filters.anio, filters.periodicidad, filters.responsable]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);


  const openForm = (row?: MantenimientoInfraestructura) => {
    const year = data?.meta.selected_year ?? data?.meta.current_year ?? 2026;
    setFormError(null);
    setForm({ open: true, id: row?.id ?? null, value: row ? { ...row } : emptyInfra(year) });
  };
  const submitForm = async () => {
    setSubmitting(true); setFormError(null);
    try { await saveInfra(form.id, form.value); setForm({ open: false, id: null, value: emptyInfra(data?.meta.selected_year ?? 2026) }); await load(true); }
    catch (requestError) { setFormError(getCertificationRequestError(requestError, "No se pudo guardar el registro.")); }
    finally { setSubmitting(false); }
  };
  const submitClone = async () => {
    setSubmitting(true); setFormError(null);
    try { await createInfraYear(clone.source, clone.target, clone.fields); setClone({ ...clone, open: false }); setFilters((current) => ({ ...current, anio: String(clone.target) })); }
    catch (requestError) { setFormError(getCertificationRequestError(requestError, "No se pudo crear la plantilla anual.")); }
    finally { setSubmitting(false); }
  };
  const remove = async () => {
    setSubmitting(true);
    try {
      const removingYear = deleteYear !== null;
      if (deleteRow) await deleteInfra(deleteRow.id);
      if (deleteYear !== null) await deleteInfraYear(deleteYear);
      setDeleteRow(null);
      setDeleteYear(null);

      if (removingYear) {
        lastLoadedKeyRef.current = "";
        setFilters((current) => ({ ...current, anio: "" }));
      } else {
        await load(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const rows = data?.data.registros ?? [];
  const controls = data?.data.control_modificaciones ?? [];
  const columns: Iso900114001TableColumn<MantenimientoInfraestructura>[] = [
    {
      key: "instruccion",
      header: "Instrucción",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.instruccion ?? undefined}>
          {row.instruccion || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "elemento",
      header: "Elemento",
      className: "iso9001-extra-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.elemento ?? undefined}>
          {row.elemento || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "realizado",
      header: "Realizado por",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.realizado_por ?? undefined}>
          {row.realizado_por || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "periodicidad",
      header: "Periodicidad",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.periodicidad ?? undefined}>
          {row.periodicidad || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    ...months.map((month) => ({
      key: month,
      header: month.slice(0, 3).toUpperCase(),
      className: "iso9001-month-column",
      render: (row: MantenimientoInfraestructura) => row[month] || "",
    })),
    {
      key: "observaciones",
      header: "Observaciones",
      className: "iso9001-extra-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.observaciones ?? undefined}>
          {row.observaciones || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
  ];

  return <section className="iso9001-section-stack">
    <Iso900114001SectionHeader
      eyebrow="Calidad y medioambiente"
      title="Mantenimiento de infraestructuras"
      description="Planifica y registra el mantenimiento anual, con creación de plantillas a partir de cualquier ejercicio existente."
      onBack={onBack}
      onRefresh={() => void load(true)}
      refreshing={loading || refreshing}
    />
    <div className="iso9001-overview-grid"><Iso900114001OverviewCard icon={Building2} label="Registros del año" value={data?.meta.total ?? 0} /><Iso900114001OverviewCard icon={Wrench} label="Año seleccionado" value={data?.meta.selected_year ?? "—"} /><Iso900114001OverviewCard icon={ClipboardList} label="Control documental" value={controls.length} /></div>
    <Iso900114001Tabs tabs={[{ id: "mantenimiento", label: "Mantenimiento infraestructuras", count: rows.length }, { id: "control", label: "Control documental", count: controls.length }]} activeTab={tab} onChange={setTab} ariaLabel="Mantenimiento de infraestructuras" />
    {tab === "control" ? <CalidadControlDocumentalPanel rows={controls} loading={loading} error={error} baseEndpoint={infraControlEndpoint} onReload={() => load(true)} /> : <>
      <Iso900114001Filters
        title="Filtrar mantenimiento"
        count={rows.length}
        onReset={() => {
          setSearchInput("");
          setDebouncedSearch("");
          setFilters({ anio: String(data?.meta.selected_year ?? ""), responsable: "", periodicidad: "" });
        }}
        actions={(
          <>
            <button
              type="button"
              className="calidad-primary-button"
              onClick={() => setClone({
                open: true,
                source: data?.meta.selected_year ?? 2025,
                target: data?.meta.suggested_new_year ?? 2026,
                fields: copyOptions.map((item) => item.id),
              })}
            >
              <CalendarPlus size={16} />
              Crear plantilla anual
            </button>
            <button
              type="button"
              className="iso9001-filter-danger-button"
              disabled={!data?.meta.selected_year}
              onClick={() => data?.meta.selected_year && setDeleteYear(data.meta.selected_year)}
            >
              <Trash2 size={16} />
              Eliminar año
            </button>
          </>
        )}
      >
        <Iso900114001Select label="Año" value={filters.anio} onChange={(event) => setFilters({ ...filters, anio: event.target.value })}>{(data?.filter_options.anios ?? []).map((year) => <option key={year} value={year}>{year}</option>)}</Iso900114001Select>
        <Iso900114001Input label="Búsqueda" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Instrucción, elemento u observaciones" />
        <Iso900114001Select label="Realizado por" value={filters.responsable} onChange={(event) => setFilters({ ...filters, responsable: event.target.value })}><option value="">Todos</option>{(data?.filter_options.responsables ?? []).map((value) => <option key={value}>{value}</option>)}</Iso900114001Select>
        <Iso900114001Select label="Periodicidad" value={filters.periodicidad} onChange={(event) => setFilters({ ...filters, periodicidad: event.target.value })}><option value="">Todas</option>{(data?.filter_options.periodicidades ?? []).map((value) => <option key={value}>{value}</option>)}</Iso900114001Select>
      </Iso900114001Filters>
      {refreshing ? <div className="iso9001-background-loading"><Loader2 size={16} className="animate-spin" />Actualizando registros...</div> : null}
      {data && error ? <div className="reqleg-inline-error">{error}</div> : null}
      <Iso900114001CrudTable title={`Mantenimiento ${data?.meta.selected_year ?? ""}`} rows={rows} columns={columns} getRowKey={(row) => row.id} loading={loading} error={data ? null : error} createLabel="Añadir registro" emptyTitle="No hay registros para el año seleccionado" onCreate={() => openForm()} onEdit={openForm} onDelete={setDeleteRow} onRetry={() => void load(true)} />
    </>}
    <Iso900114001Modal open={form.open} title={form.id ? "Editar mantenimiento" : "Nuevo mantenimiento"} wide submitting={submitting} error={formError} onClose={() => setForm({ ...form, open: false })} onSubmit={() => void submitForm()} footer={<><button type="button" className="calidad-back-button" onClick={() => setForm({ ...form, open: false })}>Cancelar</button><button type="submit" className="calidad-primary-button" disabled={submitting}>{submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Guardar</button></>}>
      <Iso900114001Input label="Año" type="number" required value={form.value.anio} onChange={(event) => setForm({ ...form, value: { ...form.value, anio: Number(event.target.value) } })} />
      <Iso900114001Input label="Instrucción" value={form.value.instruccion ?? ""} onChange={(event) => setForm({ ...form, value: { ...form.value, instruccion: event.target.value } })} />
      <Iso900114001Textarea label="Elemento" fullWidth rows={3} value={form.value.elemento ?? ""} onChange={(event) => setForm({ ...form, value: { ...form.value, elemento: event.target.value } })} />
      <Iso900114001Input label="Realizado por" value={form.value.realizado_por ?? ""} onChange={(event) => setForm({ ...form, value: { ...form.value, realizado_por: event.target.value } })} />
      <Iso900114001Input label="Periodicidad" value={form.value.periodicidad ?? ""} onChange={(event) => setForm({ ...form, value: { ...form.value, periodicidad: event.target.value } })} />
      <div className="iso9001-field full"><span>Áreas de aplicación</span><div className="iso9001-checkbox-grid">{applicability.map((key) => <Iso900114001Checkbox key={key} label={areaLabels[key]} checked={form.value[key]} onChange={(checked) => setForm({ ...form, value: { ...form.value, [key]: checked } })} />)}</div></div>
      <div className="iso9001-field full"><span>Seguimiento mensual</span><div className="iso9001-month-fields">{months.map((month) => <Iso900114001Input key={month} label={month[0].toUpperCase() + month.slice(1)} value={form.value[month] ?? ""} onChange={(event) => setForm({ ...form, value: { ...form.value, [month]: event.target.value || null } })} />)}</div></div>
      <Iso900114001Textarea label="Observaciones" fullWidth rows={4} value={form.value.observaciones ?? ""} onChange={(event) => setForm({ ...form, value: { ...form.value, observaciones: event.target.value } })} />
    </Iso900114001Modal>
    <Iso900114001Modal open={clone.open} title="Crear plantilla anual" eyebrow="Copia selectiva" submitting={submitting} error={formError} onClose={() => setClone({ ...clone, open: false })} onSubmit={() => void submitClone()} footer={<><button type="button" className="calidad-back-button" onClick={() => setClone({ ...clone, open: false })}>Cancelar</button><button type="submit" className="calidad-primary-button" disabled={submitting || clone.fields.length === 0}>{submitting ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}Crear año</button></>}>
      <Iso900114001Select label="Año origen" value={clone.source} onChange={(event) => setClone({ ...clone, source: Number(event.target.value) })}>{(data?.filter_options.anios ?? []).map((year) => <option key={year} value={year}>{year}</option>)}</Iso900114001Select>
      <Iso900114001Input label="Año destino" type="number" min={2000} max={2100} value={clone.target} onChange={(event) => setClone({ ...clone, target: Number(event.target.value) })} />
      <div className="iso9001-field full"><span>Campos que se copiarán</span><div className="iso9001-copy-actions"><button type="button" className="calidad-back-button" onClick={() => setClone({ ...clone, fields: copyOptions.map((item) => item.id) })}>Seleccionar todo</button><button type="button" className="calidad-back-button" onClick={() => setClone({ ...clone, fields: [] })}>Quitar todo</button></div><div className="iso9001-checkbox-grid">{copyOptions.map((option) => <Iso900114001Checkbox key={option.id} label={option.label} checked={clone.fields.includes(option.id)} onChange={(checked) => setClone({ ...clone, fields: checked ? [...clone.fields, option.id] : clone.fields.filter((field) => field !== option.id) })} />)}</div></div>
    </Iso900114001Modal>
    <Iso900114001ConfirmDialog open={deleteRow !== null || deleteYear !== null} title={deleteYear !== null ? `Eliminar el año ${deleteYear}` : "Eliminar mantenimiento"} description={deleteYear !== null ? "Se eliminarán todos los registros de este año." : "Se eliminará el registro seleccionado."} confirming={submitting} onClose={() => { setDeleteRow(null); setDeleteYear(null); }} onConfirm={() => void remove()} />
  </section>;
}
